// src/pages/Tasks.js
import { useState, useEffect } from "react";
import EntityPicker from "../components/EntityPicker";
import { Timestamp } from "firebase/firestore";
import { fmtDate } from "../components/UI";
import { watchTasks, watchContacts, updateTask, getContact, createTask } from "../lib/db";
import AlertBanner from "../components/AlertBanner";
import EmptyState from "../components/EmptyState";
import { CheckSquare } from "lucide-react";

const PRIORITY_ORDER = { urgent: 0, high: 1, medium: 2, low: 3 };
const PRIORITY_COLORS = { urgent: "var(--red)", high: "var(--amber)", medium: "var(--accent)", low: "var(--text-3)" };

function dueMs(t) {
  const d = t?.dueAt;
  if (!d) return null;
  if (d?.toMillis) return d.toMillis();
  if (d instanceof Date) return d.getTime();
  const x = new Date(d).getTime();
  return Number.isNaN(x) ? null : x;
}

export default function Tasks({ onOpenContact }) {
  const [tasks, setTasks] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [filter, setFilter] = useState("open");
  const [loadError, setLoadError] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [creating, setCreating] = useState(false);
  const [newForm, setNewForm] = useState({
    title: "",
    contactId: "",
    priority: "medium",
    dueDate: "",
    owner: "",
  });

  useEffect(() => {
    return watchContacts(setContacts, () => {});
  }, []);

  useEffect(() => {
    return watchTasks(
      (data) => {
        setLoadError(null);
        setTasks(data);
      },
      () => setLoadError("Could not load tasks (check Firestore rules).")
    );
  }, []);

  async function completeTask(id, task) {
    try {
      await updateTask(id, { status: "completed" });
    } catch (e) {
      console.error(e);
    }
  }

  function openLinkedContact(t) {
    if (!t.contactId) return;
    getContact(t.contactId).then((c) => c && onOpenContact(c));
  }

  async function submitNewTask(e) {
    e.preventDefault();
    if (!newForm.title.trim()) return;
    setCreating(true);
    try {
      const c = newForm.contactId ? contacts.find((x) => x.id === newForm.contactId) : null;
      let dueAt;
      if (newForm.dueDate) {
        const d = new Date(`${newForm.dueDate}T12:00:00`);
        if (!Number.isNaN(d.getTime())) dueAt = Timestamp.fromDate(d);
      }
      await createTask({
        title: newForm.title.trim(),
        contactId: newForm.contactId || null,
        contactName: c?.name || "",
        companyName: c?.companyName || "",
        priority: newForm.priority || "medium",
        owner: newForm.owner?.trim() || "",
        ...(dueAt ? { dueAt } : {}),
      });
      if (newForm.contactId) {
        try {
          const rec = [newForm.contactId, ...JSON.parse(localStorage.getItem("crm_recent_contacts") || "[]")].filter((id, i, a) => a.indexOf(id) === i).slice(0, 12);
          localStorage.setItem("crm_recent_contacts", JSON.stringify(rec));
        } catch { /* ignore */ }
      }
      setNewForm({ title: "", contactId: "", priority: "medium", dueDate: "", owner: "" });
      setShowNew(false);
    } catch (err) {
      console.error(err);
    } finally {
      setCreating(false);
    }
  }

  const filtered = tasks
    .filter((t) => (filter === "all" ? true : t.status === filter))
    .sort((a, b) => (PRIORITY_ORDER[a.priority] || 9) - (PRIORITY_ORDER[b.priority] || 9));

  const openCount = tasks.filter((t) => t.status === "open").length;
  const urgentCount = tasks.filter((t) => t.status === "open" && t.priority === "urgent").length;

  return (
    <div className="page-wrap">
      <header className="page-header">
        <h1 className="page-header-title">Task inbox</h1>
        <p className="page-header-desc">{openCount} open · {urgentCount} urgent</p>
      </header>
      {loadError && <AlertBanner variant="error">{loadError}</AlertBanner>}
      <div style={{ display: "flex", gap: 10, marginBottom: 20, alignItems: "center" }}>
        <div style={{ flex: 1 }}>
          {urgentCount > 0 && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                padding: "9px 14px",
                borderRadius: 9,
                marginBottom: 12,
                background: "var(--red-dim)",
                border: "0.5px solid var(--red)",
                fontSize: 13,
                color: "var(--red)",
              }}
            >
              <span>!</span>
              <span>
                <strong>
                  {urgentCount} urgent task{urgentCount > 1 ? "s" : ""}
                </strong>{" "}
                need your attention
              </span>
            </div>
          )}
        </div>
        <button type="button" className="btn btn-primary" onClick={() => setShowNew(true)}>
          + New task
        </button>
      </div>

      <div className="filter-bar">
        {[
          ["open", "Open", openCount],
          ["completed", "Completed"],
          ["all", "All"],
        ].map(([id, label, count]) => (
          <div key={id} className={`filter-chip ${filter === id ? "active" : ""}`} onClick={() => setFilter(id)}>
            {label}
            {count > 0 ? <span style={{ marginLeft: 5, opacity: 0.7 }}>{count}</span> : null}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {filtered.length === 0 ? (
          <EmptyState icon={CheckSquare} title={`No ${filter} tasks`} description="All caught up — create a task when you need a follow-up." />
        ) : (
          <div>
            {filtered.map((t, i) => {
              const dm = dueMs(t);
              const overdue = dm != null && dm < Date.now() && t.status !== "completed";
              return (
                <div
                  key={t.id}
                  style={{
                    display: "flex",
                    alignItems: "flex-start",
                    gap: 12,
                    padding: "14px 20px",
                    borderBottom: i < filtered.length - 1 ? "0.5px solid var(--border)" : "none",
                    opacity: t.status === "completed" ? 0.55 : 1,
                  }}
                >
                  <button
                    onClick={() => completeTask(t.id, t)}
                    disabled={t.status === "completed"}
                    style={{
                      width: 18,
                      height: 18,
                      borderRadius: 5,
                      border: "1.5px solid",
                      borderColor: t.status === "completed" ? "var(--green)" : "var(--border-md)",
                      background: t.status === "completed" ? "var(--green-dim)" : "transparent",
                      flexShrink: 0,
                      marginTop: 2,
                      cursor: t.status === "completed" ? "default" : "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 10,
                      color: "var(--green)",
                      transition: "all 0.15s",
                    }}
                  >
                    {t.status === "completed" ? "✓" : ""}
                  </button>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div
                      style={{
                        fontSize: 14,
                        fontWeight: 500,
                        marginBottom: 4,
                        textDecoration: t.status === "completed" ? "line-through" : "none",
                        color: t.status === "completed" ? "var(--text-3)" : "var(--text-1)",
                      }}
                    >
                      {t.title}
                    </div>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                      <span
                        style={{
                          fontSize: 11,
                          fontWeight: 500,
                          color: PRIORITY_COLORS[t.priority] || "var(--text-3)",
                        }}
                      >
                        {t.priority || "medium"}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>·</span>
                      <span
                        onClick={() => openLinkedContact(t)}
                        style={{
                          fontSize: 12,
                          color: t.contactId ? "var(--accent)" : "var(--text-3)",
                          cursor: t.contactId ? "pointer" : "default",
                        }}
                      >
                        {t.contactName || "—"}
                      </span>
                      <span style={{ fontSize: 11, color: "var(--text-3)" }}>·</span>
                      <span style={{ fontSize: 12, color: "var(--text-3)" }}>{t.companyName || "—"}</span>
                    </div>
                  </div>

                  <div style={{ textAlign: "right", flexShrink: 0 }}>
                    {t.dueAt && (
                      <div
                        style={{
                          fontSize: 12,
                          color: t.status !== "completed" && overdue ? "var(--red)" : "var(--text-3)",
                        }}
                      >
                        {overdue ? "Overdue · " : "Due "}
                        {fmtDate(t.dueAt)}
                      </div>
                    )}
                    <div style={{ fontSize: 11, color: "var(--text-3)", marginTop: 3 }}>Assigned to you</div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showNew && (
        <div className="modal-overlay" onClick={() => !creating && setShowNew(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: 420 }}>
            <div className="modal-header">
              <div className="modal-title">New task</div>
              <button type="button" className="btn btn-ghost btn-icon" onClick={() => setShowNew(false)} disabled={creating}>
                ×
              </button>
            </div>
            <form onSubmit={submitNewTask} style={{ display: "flex", flexDirection: "column", gap: 12 }}>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Title *
                <input
                  className="input"
                  value={newForm.title}
                  onChange={(e) => setNewForm((f) => ({ ...f, title: e.target.value }))}
                  required
                  autoFocus
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Link contact
                <div style={{ marginTop: 4 }}>
                  <EntityPicker
                    contacts={contacts}
                    value={newForm.contactId}
                    onChange={(id) => setNewForm((f) => ({ ...f, contactId: id }))}
                  />
                </div>
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Assigned to (email or name)
                <input
                  className="input"
                  value={newForm.owner}
                  onChange={(e) => setNewForm((f) => ({ ...f, owner: e.target.value }))}
                  placeholder="Who should get reminders"
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Priority
                <select
                  className="input"
                  value={newForm.priority}
                  onChange={(e) => setNewForm((f) => ({ ...f, priority: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="urgent">Urgent</option>
                </select>
              </label>
              <label style={{ fontSize: 12, color: "var(--text-2)" }}>
                Due date
                <input
                  className="input"
                  type="date"
                  value={newForm.dueDate}
                  onChange={(e) => setNewForm((f) => ({ ...f, dueDate: e.target.value }))}
                  style={{ marginTop: 4, width: "100%" }}
                />
              </label>
              <div className="modal-footer" style={{ marginTop: 8 }}>
                <button type="button" className="btn" onClick={() => setShowNew(false)} disabled={creating}>
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary" disabled={creating}>
                  {creating ? "Saving…" : "Create task"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
